import { useState, useEffect } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { Search, Key, X, Folder, Loader2 } from 'lucide-react';
import { vaultAPI } from '../../services/api';

/**
 * Password Picker Modal
 * Allows selecting a password to embed in articles/notes
 */
export default function PasswordPicker({ isOpen, onClose, onSelect, companyId }) {
  const [search, setSearch] = useState('');
  const [selectedPassword, setSelectedPassword] = useState(null);

  // Fetch passwords
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading } = useQuery({
    queryKey: ['vault', 'passwords', 'picker', { search: debouncedSearch, companyId }],
    queryFn: () => vaultAPI.listPasswords({ search: debouncedSearch, companyId, limit: 50 }),
    enabled: isOpen,
  });

  const passwords = data?.data?.passwords || [];

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedPassword(null);
    }
  }, [isOpen]);

  const handleSelect = () => {
    if (selectedPassword) {
      // Return the markdown-style embed syntax
      const embedSyntax = `[[password:${selectedPassword.id}:${selectedPassword.name}]]`;
      onSelect(embedSyntax, selectedPassword);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Insert Password Reference
            </h2>
            <button
              onClick={onClose}
              className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search passwords..."
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
              autoFocus
            />
          </div>
        </div>

        {/* Password List */}
        <div className="overflow-y-auto max-h-[50vh] p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : passwords.length === 0 ? (
            <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
              <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No passwords found</p>
              {search && <p className="text-sm mt-1">Try a different search term</p>}
            </div>
          ) : (
            <div className="space-y-2">
              {passwords.map((password) => (
                <button
                  key={password.id}
                  onClick={() => setSelectedPassword(password)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    selectedPassword?.id === password.id
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500'
                      : 'bg-neutral-50 dark:bg-neutral-700/50 border-2 border-transparent hover:border-neutral-300 dark:hover:border-neutral-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedPassword?.id === password.id
                        ? 'bg-amber-100 dark:bg-amber-900/40'
                        : 'bg-white dark:bg-neutral-600'
                    }`}>
                      <Key className={`w-4 h-4 ${
                        selectedPassword?.id === password.id
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-neutral-500 dark:text-neutral-400'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-900 dark:text-white truncate">
                        {password.name}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {password.folder_name && (
                          <span className="flex items-center gap-1">
                            <Folder className="w-3 h-3" />
                            {password.folder_name}
                          </span>
                        )}
                        {password.password_type && (
                          <span className="px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded">
                            {password.password_type.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      {password.description && (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 truncate">
                          {password.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {selectedPassword ? (
              <>Selected: <span className="font-medium text-neutral-700 dark:text-neutral-300">{selectedPassword.name}</span></>
            ) : (
              'Select a password to embed'
            )}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedPassword}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-neutral-300 dark:disabled:bg-neutral-700 disabled:text-neutral-500 text-white rounded-lg transition-colors"
            >
              Insert Reference
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
